/**
 * Standard error handler for API communication errors
 * Provides consistent error handling and user messaging
 */
// Track recent errors to prevent duplicates
let lastErrorTime = 0;
let lastErrorMessage = '';

export const handleAPIError = (error, setMessages, setThreadId, setIsSending) => {
  console.error("API Error:", error);
  
  // Prevent duplicate error messages within 2 seconds
  const now = Date.now();
  const errorKey = error.message || String(error.status) || 'unknown';
  
  if (now - lastErrorTime < 2000 && lastErrorMessage === errorKey) {
    console.log('Skipping duplicate error message (within 2 seconds)');
    setIsSending(false);
    return;
  }
  
  lastErrorTime = now;
  lastErrorMessage = errorKey;
  
  // Prevent duplicate error messages - check if last message is already an error
  setMessages(prevMessages => {
    const lastMessage = prevMessages[prevMessages.length - 1];
    const errorMessages = [
      "We encountered an issue processing your request",
      "We're experiencing a temporary issue",
      "The selected resource is currently unavailable",
      "We encountered an issue with this conversation"
    ];
    
    // If last message is already an error, don't add another one
    if (lastMessage && lastMessage.sender === 'assistant' && 
        errorMessages.some(msg => lastMessage.text.includes(msg))) {
      console.log('Skipping duplicate error message (last message is already an error)');
      return prevMessages;
    }
    
    return prevMessages;
  });
  
  // For assistant not found errors
  if (error.message && error.message.includes("No assistant found")) {
    // Log technical details to console only
    const assistantIdMatch = error.message.match(/'([^']+)'/);
    const problematicAssistantId = assistantIdMatch ? assistantIdMatch[1] : 'unknown';
    console.error("Assistant not found error, resetting thread:", {
      assistantId: problematicAssistantId,
      errorMessage: error.message
    });
    
    setThreadId(null); // Reset thread ID to force new thread creation
    
    // Show user-friendly message
    setMessages(prevMessages => [
      ...prevMessages, 
      { 
        sender: 'assistant', 
        text: `The selected resource is currently unavailable. Please try a different resource or try again later.` 
      }
    ]);
  } 
  // For thread-related errors
  else if (error.message && error.message.includes("thread")) {
    // Log technical details to console only
    console.error("Thread error detected, resetting thread:", {
      errorMessage: error.message,
      status: error.status
    });
    
    setThreadId(null);
    
    // Show user-friendly message
    setMessages(prevMessages => [
      ...prevMessages, 
      { 
        sender: 'assistant', 
        text: `We encountered an issue with this conversation. Starting a new conversation for you. Please try again.` 
      }
    ]);
  }
  // For permission or authentication errors (including API key errors)
  else if (error.status === 401 || 
           error.statusCode === 401 ||
           error.type === 'authentication_error' ||
           (error.message && (
             error.message.includes("permission") || 
             error.message.includes("authorize") ||
             error.message.includes("Incorrect API key") ||
             error.message.includes("API key authentication failed") ||
             error.message.includes("invalid_api_key")
           ))) {
    // Log technical details to console only
    console.error("Authentication error details:", {
      status: error.status,
      statusCode: error.statusCode,
      type: error.type,
      message: error.message,
      originalError: error.originalError
    });
    
    // Show user-friendly message
    setMessages(prevMessages => [
      ...prevMessages, 
      { 
        sender: 'assistant', 
        text: `We're experiencing a temporary issue connecting to our service. Please try again in a moment. If the problem persists, please contact support.` 
      }
    ]);
  }
  // For all other errors
  else {
    // Log technical details to console only
    console.error("Error details:", {
      message: error.message,
      status: error.status,
      statusCode: error.statusCode,
      type: error.type,
      stack: error.stack
    });
    
    // Show user-friendly message
    setMessages(prevMessages => [
      ...prevMessages, 
      { 
        sender: 'assistant', 
        text: `We encountered an issue processing your request. Please try again. If the problem continues, please contact support.` 
      }
    ]);
  }
  
  setIsSending(false);
}; 